/**
 * In-memory fixtures for the three voter_elections analytics surfaces.
 *
 * These mirror the live backend response shapes from
 * `/voters/analytics/turnout-heatmap`, `/voters/analytics/drop-off`,
 * and `/voters/analytics/precinct-sparklines`. They exist so the React
 * views can be exercised without round-tripping to Postgres during
 * UI iteration. Flip with VITE_USE_MOCK_ANALYTICS=0 to point at the
 * live backend.
 *
 * The numbers below are deliberately realistic-looking (Platte County
 * scale, ~78k voters total split across the four precincts shown) so
 * the visual rendering is honest. They are NOT real data and should
 * never be presented to a user as such.
 */
import type {
  DropOffResponse,
  HeatmapElectionAxis,
  PrecinctSparklinesResponse,
  TurnoutHeatmapResponse,
} from '@/types/analytics';

const ELECTIONS_AXIS: HeatmapElectionAxis[] = [
  { election_date: '2014-11-04', election_type: 'General', election_year: 2014, label: 'Nov 2014 G' },
  { election_date: '2016-11-08', election_type: 'General', election_year: 2016, label: 'Nov 2016 G' },
  { election_date: '2018-11-06', election_type: 'General', election_year: 2018, label: 'Nov 2018 G' },
  { election_date: '2020-11-03', election_type: 'General', election_year: 2020, label: 'Nov 2020 G' },
  { election_date: '2022-11-08', election_type: 'General', election_year: 2022, label: 'Nov 2022 G' },
  { election_date: '2024-11-05', election_type: 'General', election_year: 2024, label: 'Nov 2024 G' },
];

// ---------------------------------------------------------------------------
// 1. Turnout heatmap mock
// ---------------------------------------------------------------------------

export const MOCK_HEATMAP: TurnoutHeatmapResponse = {
  since_year: 2014,
  election_type_filter: 'General',
  precincts: [
    { precinct_num: '34', precinct_name: '34 BARRY EAST', total_voters: 4523 },
    { precinct_num: '12', precinct_name: '12 PLATTE CITY', total_voters: 3812 },
    { precinct_num: '08', precinct_name: '08 SOUTHEAST', total_voters: 2940 },
    { precinct_num: '21', precinct_name: '21 WELDON SPRING', total_voters: 2148 },
  ],
  elections: ELECTIONS_AXIS,
  cells: [
    // Precinct 34
    { precinct_num: '34', election_date: '2014-11-04', election_type: 'General', voter_count: 1820, turnout_pct: 40.2 },
    { precinct_num: '34', election_date: '2016-11-08', election_type: 'General', voter_count: 2891, turnout_pct: 63.9 },
    { precinct_num: '34', election_date: '2018-11-06', election_type: 'General', voter_count: 2142, turnout_pct: 47.4 },
    { precinct_num: '34', election_date: '2020-11-03', election_type: 'General', voter_count: 3221, turnout_pct: 71.2 },
    { precinct_num: '34', election_date: '2022-11-08', election_type: 'General', voter_count: 2701, turnout_pct: 59.7 },
    { precinct_num: '34', election_date: '2024-11-05', election_type: 'General', voter_count: 3411, turnout_pct: 75.4 },
    // Precinct 12
    { precinct_num: '12', election_date: '2014-11-04', election_type: 'General', voter_count: 1402, turnout_pct: 36.8 },
    { precinct_num: '12', election_date: '2016-11-08', election_type: 'General', voter_count: 2443, turnout_pct: 64.1 },
    { precinct_num: '12', election_date: '2018-11-06', election_type: 'General', voter_count: 1801, turnout_pct: 47.2 },
    { precinct_num: '12', election_date: '2020-11-03', election_type: 'General', voter_count: 2702, turnout_pct: 70.9 },
    { precinct_num: '12', election_date: '2022-11-08', election_type: 'General', voter_count: 2188, turnout_pct: 57.4 },
    { precinct_num: '12', election_date: '2024-11-05', election_type: 'General', voter_count: 2904, turnout_pct: 76.2 },
    // Precinct 08
    { precinct_num: '08', election_date: '2014-11-04', election_type: 'General', voter_count: 1009, turnout_pct: 34.3 },
    { precinct_num: '08', election_date: '2016-11-08', election_type: 'General', voter_count: 1823, turnout_pct: 62.0 },
    { precinct_num: '08', election_date: '2018-11-06', election_type: 'General', voter_count: 1320, turnout_pct: 44.9 },
    { precinct_num: '08', election_date: '2020-11-03', election_type: 'General', voter_count: 2042, turnout_pct: 69.5 },
    { precinct_num: '08', election_date: '2022-11-08', election_type: 'General', voter_count: 1611, turnout_pct: 54.8 },
    { precinct_num: '08', election_date: '2024-11-05', election_type: 'General', voter_count: 2188, turnout_pct: 74.4 },
    // Precinct 21
    { precinct_num: '21', election_date: '2014-11-04', election_type: 'General', voter_count: 612, turnout_pct: 28.5 },
    { precinct_num: '21', election_date: '2016-11-08', election_type: 'General', voter_count: 1281, turnout_pct: 59.6 },
    { precinct_num: '21', election_date: '2018-11-06', election_type: 'General', voter_count: 941, turnout_pct: 43.8 },
    { precinct_num: '21', election_date: '2020-11-03', election_type: 'General', voter_count: 1422, turnout_pct: 66.2 },
    { precinct_num: '21', election_date: '2022-11-08', election_type: 'General', voter_count: 1008, turnout_pct: 46.9 },
    { precinct_num: '21', election_date: '2024-11-05', election_type: 'General', voter_count: 1503, turnout_pct: 70.0 },
  ],
};

// ---------------------------------------------------------------------------
// 2. Drop-off detector mock
// ---------------------------------------------------------------------------

export const MOCK_DROP_OFF: DropOffResponse = {
  base_year: 2020,
  compare_year: 2022,
  election_type: 'General',
  base_count: 9387,
  retained_count: 7508,
  dropped_count: 1879,
  drop_off_pct: 20.0,
  by_precinct: [
    { precinct_num: '34', precinct_name: '34 BARRY EAST',    base_count: 3221, retained_count: 2701, dropped_count: 520, drop_off_pct: 16.1 },
    { precinct_num: '12', precinct_name: '12 PLATTE CITY',   base_count: 2702, retained_count: 2188, dropped_count: 514, drop_off_pct: 19.0 },
    { precinct_num: '08', precinct_name: '08 SOUTHEAST',     base_count: 2042, retained_count: 1611, dropped_count: 431, drop_off_pct: 21.1 },
    { precinct_num: '21', precinct_name: '21 WELDON SPRING', base_count: 1422, retained_count: 1008, dropped_count: 414, drop_off_pct: 29.1 },
  ],
  sample_dropped_voter_ids: [
    12001, 12002, 12003, 12004, 12005, 12006, 12007, 12008,
    12009, 12010, 12011, 12012, 12013, 12014, 12015, 12016,
  ],
};

// ---------------------------------------------------------------------------
// 3. Precinct sparklines mock
// ---------------------------------------------------------------------------

const seriesFor = (counts: number[], totalVoters: number) =>
  ELECTIONS_AXIS.map((e, i) => ({
    election_date: e.election_date,
    election_type: e.election_type,
    voter_count: counts[i],
    turnout_pct: Math.round((counts[i] / totalVoters) * 1000) / 10,
  }));

export const MOCK_SPARKLINES: PrecinctSparklinesResponse = {
  election_type_filter: 'General',
  lookback: 6,
  elections: ELECTIONS_AXIS,
  precincts: [
    { precinct_num: '34', precinct_name: '34 BARRY EAST',    total_voters: 4523, series: seriesFor([1820, 2891, 2142, 3221, 2701, 3411], 4523) },
    { precinct_num: '12', precinct_name: '12 PLATTE CITY',   total_voters: 3812, series: seriesFor([1402, 2443, 1801, 2702, 2188, 2904], 3812) },
    { precinct_num: '08', precinct_name: '08 SOUTHEAST',     total_voters: 2940, series: seriesFor([1009, 1823, 1320, 2042, 1611, 2188], 2940) },
    { precinct_num: '21', precinct_name: '21 WELDON SPRING', total_voters: 2148, series: seriesFor([ 612, 1281,  941, 1422, 1008, 1503], 2148) },
  ],
};
